import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Plus, Minus, Trash2, Package, AlertTriangle, Loader } from 'lucide-react';
import { Platform } from '../utils/platform';
import { useBottomSheet } from '../hooks/useBottomSheet';
import botAPI from '../botAPI';
import { logger } from '../utils/logger';
import '../styles/OrderDetailModal.css';

/**
 * OrderDetailModal - Bottom Sheet для просмотра и редактирования заказа
 *
 * Функции:
 * - Просмотр деталей заказа (товары, цены, итоги)
 * - Изменение количества товаров (debounce 500ms, optimistic UI)
 * - Удаление товаров (двухшаговое подтверждение)
 * - Добавление новых товаров из каталога цикла
 * - Удаление заказа целиком (трёхшаговое подтверждение)
 *
 * Редактирование доступно только для pending заказов в активном цикле.
 */
const OrderDetailModal = ({ order, isOpen, onClose, onOrderUpdated }) => {
  const [orderDetail, setOrderDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Удаление заказа: 0 -> 1 -> 2 (три шага подтверждения)
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(0);
  // Удаление товара: null | item_id
  const [deletingItemId, setDeletingItemId] = useState(null);
  // Пикер товаров
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  // Спиннер при сохранении
  const [savingItemId, setSavingItemId] = useState(null);

  // Таймеры для debounced quantity API
  const quantityTimers = useRef({});
  // Таймер для автосброса deleteConfirmStep
  const deleteConfirmTimer = useRef(null);
  // Таймер для автосброса deletingItemId
  const deleteItemTimer = useRef(null);

  // A11y: кнопка закрытия и элемент, вернувший фокус после закрытия
  const closeBtnRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  // Bottom sheet hook
  const {
    handlers,
    sheetRef,
    dismiss,
    handleBackdropClick
  } = useBottomSheet({
    onDismiss: onClose,
    isOpen
  });

  // Статус-конфигурация (повторяет ProfilePage)
  const statusConfig = {
    pending: { label: 'Ожидает', className: 'order-status-pending' },
    paid: { label: 'Оплачен', className: 'order-status-paid' },
    confirmed: { label: 'Подтверждён', className: 'order-status-confirmed' },
    completed: { label: 'Выполнен', className: 'order-status-completed' },
    cancelled: { label: 'Отменён', className: 'order-status-cancelled' },
  };

  // Загрузка деталей заказа
  const loadDetail = useCallback(async () => {
    if (!order?.cycle_order_id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await botAPI.getOrderDetail(order.cycle_order_id);
      setOrderDetail(data.order || data);
      logger.log('Order detail loaded:', data);
    } catch (e) {
      logger.error('Failed to load order detail:', e);
      setError('Не удалось загрузить заказ');
    } finally {
      setLoading(false);
    }
  }, [order?.cycle_order_id]);

  // При открытии загружаем детали
  useEffect(() => {
    if (isOpen && order) {
      loadDetail();
      // Сброс состояний
      setDeleteConfirmStep(0);
      setDeletingItemId(null);
      setShowProductPicker(false);
      setAvailableProducts([]);
    }
    // Cleanup при закрытии
    if (!isOpen) {
      setOrderDetail(null);
      setError(null);
      // Очистка всех таймеров
      Object.values(quantityTimers.current).forEach(clearTimeout);
      quantityTimers.current = {};
      if (deleteConfirmTimer.current) clearTimeout(deleteConfirmTimer.current);
      if (deleteItemTimer.current) clearTimeout(deleteItemTimer.current);
    }
  }, [isOpen, order, loadDetail]);

  // Блокируем скролл body
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (Platform.isTelegram()) {
        Platform.expand();
      }
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Получить шаг изменения количества
  const getStep = (item) => {
    return item.step_quantity || 1;
  };

  // Изменение количества товара (debounce + optimistic UI)
  const handleQuantityChange = useCallback((item, newQuantity) => {
    if (newQuantity <= 0) return;

    // Optimistic UI: обновляем локальное состояние мгновенно
    setOrderDetail(prev => {
      if (!prev) return prev;
      const updatedItems = prev.items.map(i =>
        i.item_id === item.item_id
          ? { ...i, quantity: newQuantity, line_total: newQuantity * (i.unit_final_price || i.base_price_per_unit || 0) }
          : i
      );
      return { ...prev, items: updatedItems };
    });

    Platform.hapticFeedback('light');
    setSavingItemId(item.item_id);

    // Debounced API call
    if (quantityTimers.current[item.item_id]) {
      clearTimeout(quantityTimers.current[item.item_id]);
    }

    quantityTimers.current[item.item_id] = setTimeout(async () => {
      try {
        const result = await botAPI.updateOrderItemQuantity(
          order.cycle_order_id,
          item.item_id,
          newQuantity
        );
        if (result.success) {
          // Обновляем итоги из ответа сервера
          setOrderDetail(prev => prev ? {
            ...prev,
            subtotal: result.subtotal,
            delivery_fee: result.delivery_fee,
            total_amount: result.total_amount,
            discount_amount: result.discount_amount ?? prev.discount_amount,
          } : prev);
          if (onOrderUpdated) onOrderUpdated();
        }
      } catch (e) {
        logger.error('Failed to update quantity:', e);
        // Revert на ошибке — перезагрузить
        loadDetail();
      } finally {
        setSavingItemId(null);
      }
    }, 500);
  }, [order?.cycle_order_id, loadDetail, onOrderUpdated]);

  // Удаление товара (двухшаговое подтверждение)
  const handleDeleteItem = useCallback((item) => {
    if (deletingItemId === item.item_id) {
      // Второе нажатие - удаляем
      performDeleteItem(item.item_id);
      setDeletingItemId(null);
      if (deleteItemTimer.current) clearTimeout(deleteItemTimer.current);
    } else {
      // Первое нажатие - подсвечиваем
      setDeletingItemId(item.item_id);
      Platform.hapticFeedback('warning');
      // Автосброс через 3 сек
      if (deleteItemTimer.current) clearTimeout(deleteItemTimer.current);
      deleteItemTimer.current = setTimeout(() => setDeletingItemId(null), 3000);
    }
  }, [deletingItemId]);

  const performDeleteItem = async (itemId) => {
    try {
      setSavingItemId(itemId);
      const result = await botAPI.deleteOrderItem(order.cycle_order_id, itemId);
      Platform.hapticFeedback('success');

      if (result.success) {
        // Удаляем из локального состояния
        setOrderDetail(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            items: prev.items.filter(i => i.item_id !== itemId),
            subtotal: result.subtotal,
            delivery_fee: result.delivery_fee,
            total_amount: result.total_amount,
          };
        });
        if (onOrderUpdated) onOrderUpdated();
      }
    } catch (e) {
      logger.error('Failed to delete item:', e);
      // Если последний товар — показываем специальное сообщение
      if (e.message && e.message.includes('последн')) {
        Platform.showAlert('Нельзя удалить последний товар. Удалите заказ целиком.');
      } else {
        Platform.showAlert('Не удалось удалить товар');
      }
      loadDetail();
    } finally {
      setSavingItemId(null);
    }
  };

  // Показать пикер товаров
  const handleShowProductPicker = async () => {
    setShowProductPicker(true);
    setProductsLoading(true);
    try {
      const result = await botAPI.getAvailableProducts(order.cycle_order_id);
      setAvailableProducts(result.products || result || []);
    } catch (e) {
      logger.error('Failed to load available products:', e);
      setAvailableProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  // Добавить товар из пикера
  const handleAddProduct = async (product) => {
    try {
      const quantity = product.min_order_quantity || 1;
      const result = await botAPI.addOrderItem(
        order.cycle_order_id,
        product.product_id,
        quantity
      );
      Platform.hapticFeedback('success');

      if (result.success) {
        setShowProductPicker(false);
        // Перезагрузить данные заказа для полной синхронизации
        loadDetail();
        if (onOrderUpdated) onOrderUpdated();
      }
    } catch (e) {
      logger.error('Failed to add product:', e);
      Platform.showAlert('Не удалось добавить товар');
    }
  };

  // Удаление заказа (трёхшаговое подтверждение)
  const handleDeleteOrder = async () => {
    if (deleteConfirmStep < 2) {
      setDeleteConfirmStep(prev => prev + 1);
      Platform.hapticFeedback(deleteConfirmStep === 0 ? 'warning' : 'error');
      // Автосброс через 3 сек
      if (deleteConfirmTimer.current) clearTimeout(deleteConfirmTimer.current);
      deleteConfirmTimer.current = setTimeout(() => setDeleteConfirmStep(0), 3000);
      return;
    }

    // Step 2 - удаляем
    try {
      const result = await botAPI.deleteOrder(order.cycle_order_id);
      Platform.hapticFeedback('success');
      dismiss();
      if (onOrderUpdated) onOrderUpdated();
      if (result.balance_refunded > 0) {
        setTimeout(() => {
          Platform.showAlert(
            `Заказ удалён. ${result.balance_refunded.toLocaleString('ru-RU')} бонусов возвращено на баланс.`
          );
        }, 300);
      }
    } catch (e) {
      logger.error('Failed to delete order:', e);
      setError('Не удалось удалить заказ');
      setDeleteConfirmStep(0);
    }
  };

  const handleClose = () => {
    Platform.hapticFeedback('light');
    dismiss();
  };

  // A11y: закрытие по Escape
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  // A11y: перевод фокуса в модалку при открытии, возврат на триггер при закрытии
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement;
      requestAnimationFrame(() => {
        if (closeBtnRef.current) closeBtnRef.current.focus();
      });
      return undefined;
    }
    if (previouslyFocusedRef.current && previouslyFocusedRef.current.focus) {
      previouslyFocusedRef.current.focus();
      previouslyFocusedRef.current = null;
    }
    return undefined;
  }, [isOpen]);

  if (!isOpen || !order) return null;

  const isMobile = Platform.isMobile();
  const status = statusConfig[order.order_status] || { label: order.order_status, className: 'order-status-default' };
  const isEditable = orderDetail?.is_editable;

  return (
    <div
      className="order-detail-backdrop entering"
      onClick={handleBackdropClick}
    >
      <div
        ref={sheetRef}
        className={`order-detail-sheet ${isMobile ? 'opening' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-detail-title"
        onClick={(e) => e.stopPropagation()}
        {...(isMobile ? handlers : {})}
      >
        {/* Scrollable Content */}
        <div className="sheet-content">
          {/* Handle zone (mobile drag only) */}
          <div className="order-detail-handle-zone">
            <div className="order-detail-handle" />
          </div>

          {/* Header with close button */}
          <div className="order-detail-header">
            <div className="order-detail-title-row">
              <h2 id="order-detail-title" className="order-detail-title">
                Заказ №{order.cycle_order_id}
              </h2>
              <span className={`order-status-badge ${status.className}`}>
                {status.label}
              </span>
              <button ref={closeBtnRef} className="order-detail-close-btn" onClick={handleClose} aria-label="Закрыть">
                <X size={20} />
              </button>
            </div>
            <div className="order-detail-date">
              {new Date(order.created_at).toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
              {order.cycle_name && <span> &middot; {order.cycle_name}</span>}
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="order-detail-loading">
              <Loader size={24} className="order-detail-spinner" />
              <span>Загрузка...</span>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="order-detail-error">
              <AlertTriangle size={18} />
              <span>{error}</span>
              <button onClick={loadDetail} className="order-detail-retry-btn">
                Повторить
              </button>
            </div>
          )}

          {/* Content */}
          {orderDetail && !loading && (
            <>
              {/* Editable banner */}
              {isEditable ? (
                <div className="order-edit-banner order-edit-banner--active">
                  <span>Заказ можно редактировать, пока цикл активен</span>
                </div>
              ) : (
                <div className="order-edit-banner order-edit-banner--locked">
                  <span>Заказ зафиксирован</span>
                </div>
              )}

              {/* Items list */}
              <div className="order-items-list">
                {(orderDetail.items || []).map((item) => (
                  <div
                    key={item.item_id}
                    className={`order-item-row ${deletingItemId === item.item_id ? 'order-item-row--deleting' : ''}`}
                  >
                    {/* Image */}
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt={item.product_name}
                        className="order-item-image"
                      />
                    )}
                    {!item.image_url && (
                      <div className="order-item-image-placeholder">
                        <Package size={18} />
                      </div>
                    )}

                    {/* Info */}
                    <div className="order-item-info">
                      <div className="order-item-name">{item.product_name}</div>
                      <div className="order-item-price">
                        {(item.unit_final_price || item.base_price_per_unit || 0).toLocaleString('ru-RU')} ₽/{item.unit || 'кг'}
                      </div>
                    </div>

                    {/* Editable stepper or read-only quantity */}
                    {isEditable ? (
                      <div className="order-item-controls">
                        <div className="order-item-stepper">
                          <button
                            className="order-stepper-btn"
                            onClick={() => handleQuantityChange(item, item.quantity - getStep(item))}
                            disabled={item.quantity <= getStep(item)}
                            aria-label="Уменьшить"
                          >
                            <Minus size={14} />
                          </button>
                          <span className="order-stepper-value">
                            {savingItemId === item.item_id ? (
                              <Loader size={12} className="order-detail-spinner" />
                            ) : (
                              <>{item.quantity} {item.unit || 'кг'}</>
                            )}
                          </span>
                          <button
                            className="order-stepper-btn"
                            onClick={() => handleQuantityChange(item, item.quantity + getStep(item))}
                            aria-label="Увеличить"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                        <button
                          className={`order-item-delete-btn ${deletingItemId === item.item_id ? 'order-item-delete-btn--confirm' : ''}`}
                          onClick={() => handleDeleteItem(item)}
                          aria-label={deletingItemId === item.item_id ? 'Подтвердить удаление' : 'Удалить товар'}
                        >
                          <Trash2 size={14} />
                          {deletingItemId === item.item_id && (
                            <span className="order-item-delete-label">Удалить?</span>
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="order-item-total">
                        <div className="order-item-qty">{item.quantity} {item.unit || 'кг'}</div>
                        <div className="order-item-line-total">
                          {(item.line_total || 0).toLocaleString('ru-RU')} ₽
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add product button */}
              {isEditable && !showProductPicker && (
                <button
                  onClick={handleShowProductPicker}
                  className="order-add-product-btn"
                >
                  <Plus size={16} />
                  Добавить товар
                </button>
              )}

              {/* Product picker */}
              {showProductPicker && (
                <div className="order-product-picker">
                  <div className="order-product-picker-header">
                    <h3>Добавить товар</h3>
                    <button
                      onClick={() => setShowProductPicker(false)}
                      className="order-product-picker-close"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="order-product-picker-list">
                    {productsLoading ? (
                      <div className="order-detail-loading">
                        <Loader size={20} className="order-detail-spinner" />
                        <span>Загрузка товаров...</span>
                      </div>
                    ) : availableProducts.length === 0 ? (
                      <div className="order-product-picker-empty">
                        Все доступные товары уже в заказе
                      </div>
                    ) : (
                      availableProducts.map((p) => (
                        <div key={p.product_id} className="order-product-picker-item">
                          {p.image_url ? (
                            <img src={p.image_url} alt={p.product_name || p.name_ru} className="order-product-picker-img" />
                          ) : (
                            <div className="order-product-picker-img-placeholder">
                              <Package size={16} />
                            </div>
                          )}
                          <div className="order-product-picker-info">
                            <div className="order-product-picker-name">
                              {p.product_name || p.name_ru}
                            </div>
                            <div className="order-product-picker-price">
                              {(p.cycle_price || 0).toLocaleString('ru-RU')} ₽/{p.unit || 'кг'}
                            </div>
                          </div>
                          <button
                            onClick={() => handleAddProduct(p)}
                            className="order-product-picker-add-btn"
                          >
                            Добавить
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Summary section */}
              <div className="order-summary">
                <div className="order-summary-row">
                  <span>Товары</span>
                  <span>{(orderDetail.subtotal || 0).toLocaleString('ru-RU')} ₽</span>
                </div>
                {(orderDetail.delivery_fee > 0) && (
                  <div className="order-summary-row">
                    <span>Доставка</span>
                    <span>{orderDetail.delivery_fee.toLocaleString('ru-RU')} ₽</span>
                  </div>
                )}
                {(orderDetail.discount_amount > 0) && (
                  <div className="order-summary-row order-summary-row--discount">
                    <span>Скидка</span>
                    <span>-{orderDetail.discount_amount.toLocaleString('ru-RU')} ₽</span>
                  </div>
                )}
                {(orderDetail.bonus_applied > 0) && (
                  <div className="order-summary-row order-summary-row--discount">
                    <span>Бонусы</span>
                    <span>-{orderDetail.bonus_applied.toLocaleString('ru-RU')} ₽</span>
                  </div>
                )}
                <div className="order-summary-total">
                  <span>Итого</span>
                  <span>{(orderDetail.total_amount || 0).toLocaleString('ru-RU')} ₽</span>
                </div>
              </div>

              {/* Delete order button */}
              {isEditable && (
                <button
                  onClick={handleDeleteOrder}
                  className={`order-delete-btn ${
                    deleteConfirmStep === 1 ? 'order-delete-btn--warning' :
                    deleteConfirmStep === 2 ? 'order-delete-btn--danger' : ''
                  }`}
                >
                  {deleteConfirmStep === 0
                    ? 'Удалить заказ'
                    : deleteConfirmStep === 1
                    ? 'Вы уверены?'
                    : 'Да, удалить заказ'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;
